// A place for static vals that are used across multiple components

export const MAX_UPLOAD_SIZE_BYTES = 10485760; // 10MB (bytes)
export const MAX_UPLOAD_SIZE_MB = 10;
export const LARGE_MAX_UPLOAD_SIZE_MB = 64;

// Custom Forms signature capture (TAV-3152, TAV-3197). Mirrors the API's MAX_SIGNATURE_BYTES and
// MAX_SIGNATURES_TOTAL_BYTES (tp-assist-api src/constants/files.mjs). Both are measured the way the
// API measures them: the length of the full data URI string (prefix plus base64), not the decoded
// image size. A PNG data URI is ASCII, so its string length equals the API's UTF-8 byte length.
export const MAX_SIGNATURE_SIZE_BYTES = 204800; // 200KB, one signature
export const MAX_SIGNATURES_TOTAL_BYTES = 614400; // 600KB, all signatures on one form combined

// Custom Forms rich-text images (TAV-3155), matches the API's MAX_FORM_IMAGE_BYTES.
export const MAX_FORM_IMAGE_BYTES = 1024 * 1024; // 1MiB (bytes), enforced again server-side
// Long-edge cap (CSS px) applied before upload. `prepareFormImage` downscales to this on the way in.
export const MAX_FORM_IMAGE_PX = 1600;
// Resize rungs (CSS px) offered on an inserted image, smallest to largest.
export const RICH_TEXT_IMAGE_WIDTH_LADDER = [160, 320, 480, 640];

export const BYTES_IN_KB = 1024;
export const BYTES_IN_MB = 1024 * 1024;

// SAH - CLIENT BUDGETS - Case Management as a percentage of total_amount
export const CASE_MANAGEMENT_PERCENTAGE = 0.1;
export const CASE_MANAGEMENT_DIVISOR = (1 - CASE_MANAGEMENT_PERCENTAGE) / CASE_MANAGEMENT_PERCENTAGE;

export const CASE_MANAGEMENT_NOTE_TYPE_ID = 73;

// Group Appointments - label shown on the parent's Care Workers list for a child appointment that
// shares the parent's group client but has no worker assigned yet, so an unfilled slot is visible
// to the coordinator rather than silently absent.
export const UNASSIGNED_WORKER_LABEL = 'TO BE FILLED';

export const NO_BUDGET_WARNING = 'NO_BUDGET';
export const STALE_BUDGET_WARNING = 'STALE_BUDGET';
export const NOT_FINALISED_ERROR = 'NOT_FINALISED';

// Polling interval for claim validation status (in milliseconds - 3 seconds)
export const VALIDATION_POLL_INTERVAL_MS = 1000 * 3;

// Hard timeout for claim validation to prevent indefinite polling (in milliseconds - 3 minutes)
export const VALIDATION_TIMEOUT_MS = 1000 * 60 * 3;

// Page size for paginated API requests
export const PAGE_SIZE = 100;
export const PAGE_SIZE_SM = 20;

// Page size for the LOAD MORE pattern — used as both the initial limit and the
// increment per click by list components (Claims, Statements, ClientSignups,
// PaymentBatches, MandateMatching). Mirrors the tp-assist-api DEFAULT_ROW_LIMIT.
export const LOAD_MORE_PAGE_SIZE = 30;

// Document Engine
// Mirrors the document engine's CLAIM_ITEMS_MAX_ROWS (tp-assist-document-engine
// src/generators/claimItemsDashboardCsv.ts), which enforces the limit regardless.
export const SAH_CLAIM_ITEMS_CSV_MAX_ROWS = 500000;
// Mirrors the document engine's SAH_STATEMENT_SUMMARY_MAX_ROWS (tp-assist-document-engine
// src/generators/sahStatementSummaryCsv.ts), which enforces the limit regardless.
export const SAH_STATEMENT_SUMMARY_CSV_MAX_ROWS = 250000;

export const DOCUMENT_TYPES = {
  CO_CONTRIBUTION: {
    SINGLE: 'client-co-contribution',
    COMBINED: 'client-co-contributions',
    CSV: 'client-co-contributions-csv',
  },
  SAH_CLAIM: {
    CSV: 'sah-claim-csv',
  },
  SAH_CLAIM_ITEMS: {
    CSV: 'sah-claim-items-dashboard-csv',
  },
  SAH_ACFR_ITEMS: {
    CSV: 'sah-acfr-items-csv',
  },
  SAH_STATEMENT_SUMMARY: {
    CSV: 'sah-statement-summary-csv',
  },
  SAH_STATEMENT: {
    SINGLE: 'sah-statement',
    COMBINED: 'sah-statements',
  },
  SAH_BUDGET: {
    SINGLE: 'sah-budget',
    PRINT_TO_SIGN: 'sah-budget-print-to-sign',
  },
  CUSTOM_FORM: {
    SINGLE: 'custom-form',
  },
  APPOINTMENT: {
    PDF: 'appointment-pdf',
  },
  BULK_COPY: {
    PREVIEW_CSV: 'bulk-copy-preview-impact-csv',
    RUN_CSV: 'bulk-copy-run-impact-csv',
  },
} as const;
